import Image from "next/image";
import { Button } from "@/components/ui/button";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";
import { cn, renderRichText } from "@/lib/utils";

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterProps {
  logoSrc?: string;
  logoAlt?: string;
  disclosureRichText?: string;
  disclaimer?: string;
  copyright?: string;
  formNumber?: string;
  /** Pipe-separated legal links rendered in the footer bottom row. */
  links?: FooterLink[];
  colorScheme?: ColorScheme;
}

const DEFAULT_LINKS: FooterLink[] = [
  {
    label: "Privacy",
    href: "https://www.coloniallife.com/about/privacy-policy",
  },
  {
    label: "Legal Notices",
    href: "https://www.coloniallife.com/about/legal-notices",
  },
  {
    label: "Compensation Disclosure",
    href: "https://www.coloniallife.com/about/compensation-disclosure",
  },
  {
    label: "Accessibility",
    href: "https://www.coloniallife.com/about/accessibility",
  },
  {
    label: "Do not sell or share my personal information",
    href: "https://www.coloniallife.com/about/privacy-policy#do-not-sell",
  },
];

export default function Footer({
  logoSrc = "/cl-logo-white.svg",
  logoAlt = "Colonial Life",
  disclaimer = "Insurance products are underwritten by Colonial Life & Accident Insurance Company, Columbia, SC.",
  disclosureRichText,
  copyright = "© 2026 Colonial Life & Accident Insurance Company. All rights reserved. Colonial Life is a registered trademark and marketing brand of Colonial Life & Accident Insurance Company.",
  formNumber = "NS-15375",
  links = DEFAULT_LINKS,
  colorScheme = "navy",
}: FooterProps) {
  const scheme = COLOR_SCHEMES[colorScheme];

  return (
    <footer className={cn("px-6 py-10 md:py-12", scheme.bg)}>
      <div className="space-y-6 max-w-content mx-auto">
        {disclosureRichText && (
          <div className={cn("text-body-sm max-w-2xl", scheme.body)}>
            {renderRichText(disclosureRichText)}
          </div>
        )}
        {/* Logo */}
        <Image
          src={logoSrc}
          alt={logoAlt}
          width={150}
          height={32}
          loading="eager"
          style={{ height: "4rem", width: "auto" }}
        />

        {/* Disclaimer */}
        {disclaimer && (
          <p className={cn("text-body-sm max-w-2xl", scheme.body)}>
            {disclaimer}
          </p>
        )}

        {/* Copyright */}
        {copyright && (
          <p className={cn("text-body-sm max-w-2xl", scheme.body)}>
            {copyright}
          </p>
        )}

        {/* Form number */}
        {formNumber && (
          <p className={cn("text-body-sm", scheme.body)}>{formNumber}</p>
        )}

        {/* Legal links */}
        {links && links.length > 0 && (
          <nav aria-label="Footer legal links">
            <ul className="flex flex-wrap gap-x-0 gap-y-1">
              {links.map((link, i) => (
                <li key={i} className="flex items-center">
                  {i > 0 && (
                    <span
                      className={cn("mx-1 select-none", scheme.body)}
                      aria-hidden
                    >
                      |
                    </span>
                  )}
                  <Button
                    variant="link"
                    nativeButton={false}
                    render={
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    }
                    className={cn(
                      "h-auto p-0 text-body-sm underline-offset-2 underline hover:no-underline",
                      scheme.body,
                    )}
                  >
                    {link.label}
                  </Button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </footer>
  );
}
